import addIcon from '@iconify/icons-material-symbols/add';
import type { ComponentProps } from 'react';
import type {
  FieldValues,
  Path,
  PathValue,
  UseControllerProps,
} from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FileAnchor } from '#pie/components/file-anchor/FileAnchor';
import { InputField } from '#pie/components/input-field/InputField';
import { InputUploadButton } from '#pie/components/input-upload-button/InputUploadButton';
import { MAX_FILE_SIZE } from '#pie/constants/maxFileSize';
import { formatBytes } from '#pie/utils/files';
import { cn } from '#pie/utils/TailwindUtils';
import { useExtendedRules } from '../formHelpers';

interface Props<T extends FieldValues>
  extends Pick<
      UseControllerProps<T>,
      'disabled' | 'name' | 'rules' | 'defaultValue'
    >,
    Pick<
      ComponentProps<typeof InputUploadButton>,
      'className' | 'acceptedFiles'
    > {
  label: string;
}

/* istanbul ignore next -- @preserve*/
const isFile = (value: unknown): value is File =>
  (value as File)?.name !== undefined;

export const FormFileMultiple = <T extends FieldValues>({
  name,
  label,
  rules: originalRules,
  className,
  acceptedFiles,
}: Props<T>) => {
  const { t } = useTranslation();
  const rules = useExtendedRules({
    validate: (value) => {
      /* istanbul ignore if -- @preserve*/
      if (Array.isArray(value)) {
        for (const file of (value as File[]) ?? []) {
          if (!isFile(file)) return false;
          if (file.size > MAX_FILE_SIZE)
            return t('zod.errors.file_too_large', {
              size: formatBytes(MAX_FILE_SIZE),
            });
        }
      }
    },
    ...originalRules,
  });

  const { setValue } = useFormContext<T>();
  const {
    fieldState: { error },
    field: { value, onChange },
  } = useController<T>({
    name,
    rules,
  });

  const multipleValue = (value as File[]) ?? [];

  return (
    <InputField
      isRequired={!!rules?.required}
      name={name}
      label={label}
      error={error?.message}
    >
      <div className={cn('flex flex-col gap-2', className)}>
        {multipleValue.map((file, index) => (
          <FileAnchor
            key={index}
            fileName={file.name}
            fileSize={file.size}
            onDelete={() => {
              setValue(
                name,
                multipleValue.filter((_, i) => i !== index) as PathValue<
                  T,
                  Path<T>
                >,
              );
            }}
          />
        ))}
        <InputUploadButton
          icon={addIcon}
          className="w-fit"
          multiple
          acceptedFiles={acceptedFiles}
          onChange={(value) => {
            onChange(value);
          }}
          variant="tertiarySubtleIcon"
        >
          {t('common.attachments.choose_files_other')}
        </InputUploadButton>
      </div>
    </InputField>
  );
};
